import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { useDisclosure, useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { Badge, Group, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { ScrollArea } from '@repletoryreach/sdk-ui/scroll';
import { AppShellContext, type NavbarMode } from '@repletoryreach/sdk-ui/nav';
import { Sidebar } from '../Sidebar';
import { HeaderBar } from '../HeaderBar';
import { RouteErrorBoundary } from '../../components/RouteErrorBoundary';
import { useT } from '../../../i18n/I18nProvider';
import './shell.css';
import { BrandLogo } from '../../../components/BrandLogo';
import { BrandWordmark } from '../../../components/BrandWordmark';

const HEADER_HEIGHT = 64;
const NAVBAR_WIDTH_FULL = 268;
const NAVBAR_WIDTH_ICON = 72;
const DRAWER_WIDTH = 296;

/** ≤768px switches the sidebar to an off-canvas drawer (W33). Mirrors `shell.css`. */
export const MOBILE_MEDIA_QUERY = '(max-width: 768px)'; // i18n-ignore: CSS media query, not copy

/**
 * Admin shell: bespoke layout instead of sdk-ui's `<AppShell>` composite.
 * Three regions, all `position: fixed` so the page never produces document
 * scroll: header (top), sidebar (left), main (rest). The design system's
 * scroll engine (Glide) draws the bars inside the sidebar and the main area;
 * the scrolling itself is the browser's.
 *
 * At ≤768px the sidebar region is not rendered; the same `<Sidebar>` mounts
 * inside an sdk-ui `<Drawer>` opened by the header burger (sdk-utils
 * `useDisclosure`). It closes on navigation and when the viewport grows.
 *
 * The shell still provides `AppShellContext` so `<AppShell.NavLink>` inside
 * Sidebar reads the right `navbarMode` and adapts label visibility.
 */
export function AdminShell() {
  const t = useT();
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const setSidebarOpen = useAppStore((s) => s.setSidebarOpen);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [navOpened, { open: openNav, close: closeNav }] = useDisclosure(false);
  const { pathname } = useLocation();

  useEffect(() => { closeNav(); }, [pathname, closeNav]);
  useEffect(() => { if (!isMobile) closeNav(); }, [isMobile, closeNav]);

  // Phones always get full labels inside the drawer; desktop keeps the persisted icon/full mode.
  const navbarMode: NavbarMode = isMobile || sidebarOpen ? 'full' : 'icon';
  const navbarWidth = isMobile ? 0 : sidebarOpen ? NAVBAR_WIDTH_FULL : NAVBAR_WIDTH_ICON;

  const shellCtx = useMemo(
    () => ({
      layout: 'full' as const,
      paddingX: 'lg' as const,
      paddingY: 'lg' as const,
      maxWidth: 'xl' as const,
      navbarMode,
      setNavbarMode: (m: NavbarMode) => setSidebarOpen(m === 'full'),
      navbarWidth: { icon: NAVBAR_WIDTH_ICON, compact: 96, full: NAVBAR_WIDTH_FULL },
      headerHeight: HEADER_HEIGHT,
      footerHeight: 0,
      asideWidth: 0,
      hasNavbar: !isMobile,
      hasAside: false
    }),
    [navbarMode, setSidebarOpen, isMobile]
  );

  return (
    <AppShellContext.Provider value={shellCtx}>
      <div
        className={`admin-shell${isMobile ? ' admin-shell--mobile' : ''}`}
        style={{
          ['--admin-navbar-width' as string]: `${navbarWidth}px`,
          ['--admin-header-height' as string]: `${HEADER_HEIGHT}px`
        }}
      >
        <header className="admin-shell__header rr-app-shell__header">
          <HeaderBar mobile={isMobile} menuOpened={navOpened} onMenuClick={openNav} />
        </header>

        {!isMobile && (
          <aside className="admin-shell__sidebar rr-app-shell__navbar">
            <ScrollArea className="admin-shell__sidebar-scroll" axis="y" overscroll="contain">
              <Sidebar />
            </ScrollArea>
          </aside>
        )}

        <main className="admin-shell__main">
          <ScrollArea className="admin-shell__main-scroll">
            <div className="admin-shell__main-inner">
              <RouteErrorBoundary>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </ScrollArea>
        </main>
      </div>

      {isMobile && (
        <Drawer
          opened={navOpened}
          onClose={closeNav}
          position="left"
          size={DRAWER_WIDTH}
          className="admin-shell__drawer"
          title={
            <Group gap="sm" align="center" wrap="nowrap">
              <BrandLogo size={30} style={{ color: 'var(--rr-color-primary)', flexShrink: 0 }} />
              <Text fw={800} role="subtitle"><BrandWordmark /></Text>
              <Badge color="orange" size="xs" variant="light">{t('shell.brand.badge')}</Badge>
            </Group>
          }
        >
          <Sidebar onNavigate={closeNav} />
        </Drawer>
      )}
    </AppShellContext.Provider>
  );
}
